import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuthStore } from '@/state/authStore';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingState } from '@/components/Spinner';
import type { SessionStatus } from '@/types/session';

const STATUS_OPTIONS = [
  { value: '', label: 'すべてのステータス' },
  { value: 'transcribing', label: '文字起こし中' },
  { value: 'transcribing_failed', label: '文字起こし失敗' },
  { value: 'section_config_pending', label: 'セクション設定待ち' },
  { value: 'ai_evaluating', label: 'AI 評価中' },
  { value: 'ai_evaluation_failed', label: 'AI 評価失敗' },
  { value: 'feedback_pending', label: 'フィードバック待ち' },
  { value: 'feedback_in_progress', label: 'レビュー中' },
  { value: 'coach_ack_pending', label: '確認待ち' },
  { value: 'completed', label: '完了' },
];

interface Session {
  id: string; client_name: string; coach_name: string;
  day_type: string; serial_no: number; status: SessionStatus;
  outcome: string | null; session_date: string | null; updated_at: string;
  requires_my_action: boolean;
}

export function SessionListPage() {
  const navigate = useNavigate();
  const user = useAuthStore(s => s.currentUser);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [myOnly, setMyOnly] = useState(false);
  const isFeedbacker = user?.is_feedbacker && !user?.is_admin;

  const fetchSessions = (status: string) => {
    setLoading(true);
    api.get('/sessions', { params: status ? { status } : {} })
      .then(r => { setSessions(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchSessions(statusFilter); }, [statusFilter]);

  const handleRowClick = (s: Session) => {
    if (['completed', 'coach_ack_pending', 'feedback_pending', 'feedback_in_progress'].includes(s.status)) {
      navigate(`/sessions/${s.id}/feedback`);
    } else if (s.status === 'section_config_pending') {
      navigate(`/sessions/${s.id}/sections`);
    } else {
      navigate(`/sessions/${s.id}/status`);
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">セッション一覧</h1>
        <div className="flex items-center gap-3">
          {isFeedbacker && (
            <button type="button" onClick={() => setMyOnly(!myOnly)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                myOnly ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}>
              {myOnly ? '担当分のみ表示中' : '担当分のみ'}
            </button>
          )}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm">
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="p-8 flex justify-center"><LoadingState /></div>
      ) : (() => {
        const displayed = myOnly ? sessions.filter(s => s.requires_my_action) : sessions;
        return (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="w-full">
            <thead className="border-b bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">実施日</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">クライアント</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">コーチ</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Day</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">ステータス</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayed.map(s => (
                <tr
                  key={s.id}
                  onClick={() => handleRowClick(s)}
                  className={`cursor-pointer ${
                    s.requires_my_action
                      ? 'bg-red-50 font-semibold hover:bg-red-100'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <td className="px-4 py-3 text-sm text-gray-700">{s.session_date ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{s.client_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{s.coach_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{s.day_type}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2">
                      <StatusBadge status={s.status} />
                      {s.requires_my_action && <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">要対応</span>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {displayed.length === 0 && <div className="p-8 text-center text-sm text-gray-400">該当するセッションはありません</div>}
        </div>
        );
      })()}
      <p className="mt-2 text-xs text-gray-400">{sessions.length} 件表示</p>
    </div>
  );
}
